export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
  ) {
    super(message);
  }
}

export async function api<T = any>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const res = await fetch(path, {
    method: init.method ?? (init.body !== undefined ? "POST" : "GET"),
    credentials: "same-origin",
    headers: init.body !== undefined ? { "content-type": "application/json" } : undefined,
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
  const text = await res.text();
  let data: any = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (!res.ok) {
    if (res.status === 401 && !path.startsWith("/api/login") && location.pathname !== "/login") {
      location.href = `/login?next=${encodeURIComponent(location.pathname + location.search)}`;
    }
    throw new ApiError(res.status, data?.error ?? `HTTP ${res.status}`, data?.code);
  }
  return data as T;
}

/** Uploads one file into a conversation, or with `fields` (agentId or subjectId) keeps it on an agent or a scope. */
export async function uploadFile<T = any>(file: File, conversationId?: string, fields: Record<string, string> = {}): Promise<T> {
  const form = new FormData();
  form.append("file", file, file.name);
  if (conversationId) form.append("conversationId", conversationId);
  for (const [key, value] of Object.entries(fields)) form.append(key, value);
  const res = await fetch("/api/files", { method: "POST", credentials: "same-origin", body: form });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, data?.error ?? `upload failed (HTTP ${res.status})`, data?.code);
  return data as T;
}

/** Posts one file on its own to a route that takes a 'file' field (an agent's avatar). */
export async function postFile<T = any>(path: string, file: File): Promise<T> {
  const form = new FormData();
  form.append("file", file, file.name);
  const res = await fetch(path, { method: "POST", credentials: "same-origin", body: form });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, data?.error ?? `upload failed (HTTP ${res.status})`, data?.code);
  return data as T;
}

export const get =<T = any>(path: string) => api<T>(path);
export const post = <T = any>(path: string, body: unknown = {}) => api<T>(path, { method: "POST", body });
export const patch = <T = any>(path: string, body: unknown) => api<T>(path, { method: "PATCH", body });
export const put = <T = any>(path: string, body: unknown) => api<T>(path, { method: "PUT", body });
export const del = <T = any>(path: string) => api<T>(path, { method: "DELETE" });
